import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Select } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { marginBreakdown } from "../../api/margin";
import {
  clientLabel,
  ClientPricingRuleClient,
  LazyProductPricingClient,
  NexpimClientClient,
  type LazyProductPricingDTO,
} from "../../api/pricing";
import { ProductClient } from "../../api/product";
import { fxRate, ReportingClient } from "../../api/reporting";
import { basePricingColumns, round2 } from "./pricingShared";

/**
 * Pricing → Products: effective margins today, and the per-client simulation.
 *
 * Without a client selected the grid shows each product's cost basis and its
 * margin target (global rule, or a product override). Picking a client adds
 * the simulation block: the server's target price, the contracted current
 * price and the delta, plus gross/net margin at that price against the
 * client's own cost profile — computed from the same aggregate percentages
 * the Vaadin grid used.
 */
export function PricingPage() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const { start: startExport, busy } = useExport();

  const pricing = useMemo(() => new LazyProductPricingClient(http), [http]);
  const clients = useMemo(() => new NexpimClientClient(http), [http]);
  const rules = useMemo(() => new ClientPricingRuleClient(http), [http]);
  const reporting = useMemo(() => new ReportingClient(http), [http]);
  const products = useMemo(() => new ProductClient(http), [http]);

  const [clientId, setClientId] = useState<string>("");

  // Single items in sellable statuses — the Vaadin base filter.
  const productsQuery = useQuery({
    queryKey: ["pricing", "products"],
    queryFn: async () => {
      const rows = await pricing.byTypeAndStatus(
        ["SINGLE_ITEM"],
        ["AVAILABLE", "PENDING_DISCONTINUED", "PENDING_LAUNCH"],
      );
      const effective = await pricing.effectiveCogs(rows.map((row) => row.id));
      return { rows, effective };
    },
    staleTime: 5 * 60_000,
  });

  const globalRuleQuery = useQuery({
    queryKey: ["config", "global-pricing-rule"],
    queryFn: () =>
      http.get<Array<{ id: number; type: string; value: number }>>("/global/pricing/rule"),
    staleTime: 30 * 60_000,
  });

  const clientListQuery = useQuery({
    queryKey: ["client", "active"],
    queryFn: () => clients.active(),
    staleTime: 30 * 60_000,
  });

  // The whole client block loads together: target prices, contracts, rule,
  // last-52-weeks cost profile, FX.
  const clientQuery = useQuery({
    queryKey: ["pricing", "client", clientId],
    queryFn: async () => {
      const id = Number(clientId);
      const productIds = (productsQuery.data?.rows ?? []).map((row) => row.id);
      const to = new Date();
      const from = new Date(to);
      from.setDate(from.getDate() - 364);
      const iso = (date: Date) => date.toISOString().slice(0, 10);

      const [targets, rule, fx, sales, clientRow] = await Promise.all([
        pricing.targetPrices(id, productIds),
        rules.byClient(id),
        reporting.globalFx(),
        reporting.aggregateForClient(id, { from: iso(from), to: iso(to) }),
        clients.findById(id),
      ]);
      const contracts = await products.contractPricesByClient(id).catch(() => []);
      // Current price per SKU: the contract line without a discount clause.
      const currentPrices = new Map<string, number>();
      for (const contract of contracts) {
        if ((contract.part_discount ?? -1) === -1 && contract.part?.no) {
          currentPrices.set(contract.part.no, contract.part_price ?? 0);
        }
      }
      return { targets, rule, fx, sales, clientRow, currentPrices };
    },
    enabled: clientId !== "" && Boolean(productsQuery.data),
    staleTime: 5 * 60_000,
  });

  const globalMargin = (globalRuleQuery.data ?? []).find((rule) => rule.type === "GROSS_MARGIN")?.value ?? 0;

  const cols = useMemo(() => {
    const c = defineColumns<LazyProductPricingDTO>();
    const effective = productsQuery.data?.effective ?? {};

    const base = [
      ...basePricingColumns(c),
      c.number("effective_cogs", (row) => round2(effective[String(row.id)]), {
        headerKey: "pricing.effective-cogs",
      }),
      c.custom(
        "target_margin",
        (row) => row.pricing_rule?.gross_margin ?? globalMargin,
        (row) => (
          <span className="tabular">
            {(row.pricing_rule?.gross_margin ?? globalMargin).toFixed(1)}%
            {row.pricing_rule && (
              <span className="ml-1 text-[11px] text-ink-faint">
                {t("pricing.override", "override")}
              </span>
            )}
          </span>
        ),
        { headerKey: "pricing.target-margin", priority: 3 },
      ),
    ];

    const clientData = clientQuery.data;
    if (!clientData?.clientRow) return base;

    const currency = (clientData.clientRow.cli_currency ?? "CAD") as "CAD" | "USD";
    const rate = fxRate(clientData.fx, currency);
    // The client's cost profile from their trailing-year aggregate.
    const costs = marginBreakdown(clientData.sales, rate);
    const totalCostsPercent =
      costs.sales === 0
        ? 0
        : (costs.totalPrograms + costs.discounts + costs.shippingCost + costs.advertisingCost + costs.commissions) /
          costs.sales;

    const target = (row: LazyProductPricingDTO) => clientData.targets[String(row.id)] ?? 0;

    return [
      ...base,
      c.custom(
        "client_adjustments",
        () => "",
        () => {
          const rule = clientData.rule;
          if (!rule) return <span className="text-ink-faint"></span>;
          return (
            <span className="tabular text-[12px]">
              {(rule.margin_percent_adjustment ?? 0).toFixed(1)}% /{" "}
              {(rule.margin_amount_adjustment ?? 0).toFixed(2)}
            </span>
          );
        },
        { headerKey: "pricing.client-adjustments", filter: "none", enableSorting: false },
      ),
      c.number(
        "client_current",
        (row) => clientData.currentPrices.get(row.metadata?.sku ?? "") ?? null,
        { headerKey: "pricing.client-current" },
      ),
      c.custom(
        "client_target",
        (row) => target(row),
        (row) => {
          const price = target(row);
          if (price === 0) return <span className="text-ink-faint"></span>;
          const current = clientData.currentPrices.get(row.metadata?.sku ?? "") ?? 0;
          const delta = price - current;
          return (
            <span className="tabular">
              {price}
              <span className={delta >= 0 ? "ml-1 text-[11px] text-ok" : "ml-1 text-[11px] text-risk"}>
                Δ{delta >= 0 ? "+" : ""}{Math.round(delta)}
              </span>
            </span>
          );
        },
        { headerKey: "pricing.client-target", priority: 3 },
      ),
      c.custom(
        "client_gross",
        (row) => {
          const price = target(row);
          const cogs = (effective[String(row.id)] ?? row.pricing?.cogs ?? 0) / rate;
          return price === 0 ? 0 : Math.round(((price - cogs) / price) * 1000) / 10;
        },
        (row) => {
          const price = target(row);
          if (price === 0) return <span className="text-ink-faint"></span>;
          const cogs = (effective[String(row.id)] ?? row.pricing?.cogs ?? 0) / rate;
          return <span className="tabular">{(((price - cogs) / price) * 100).toFixed(1)}%</span>;
        },
        { headerKey: "pricing.client-gross" },
      ),
      // The client's own overhead as a share of their sales, and that share
      // plus royalty plus COGS at the target price — the two figures that show
      // *why* the net margin lands where it does.
      c.custom(
        "client_avg_costs",
        () => Math.round(totalCostsPercent * 1000) / 10,
        () => <span className="tabular">{(totalCostsPercent * 100).toFixed(2)}%</span>,
        { headerKey: "pricing.client-avg-costs", filter: "none", enableSorting: false },
      ),
      c.custom(
        "product_total_costs",
        (row) => {
          const price = target(row);
          if (price === 0) return 0;
          const cogsShare = (row.pricing?.cogs ?? 0) / rate / price;
          const royalty = (row.designer?.royalty ?? 0) / 100;
          return Math.round((totalCostsPercent + royalty + cogsShare) * 1000) / 10;
        },
        (row) => {
          const price = target(row);
          if (price === 0) return <span className="text-ink-faint"></span>;
          const cogsShare = (row.pricing?.cogs ?? 0) / rate / price;
          const royalty = (row.designer?.royalty ?? 0) / 100;
          return (
            <span className="tabular">
              {((totalCostsPercent + royalty + cogsShare) * 100).toFixed(2)}%
            </span>
          );
        },
        { headerKey: "pricing.product-total-costs", filter: "none", enableSorting: false },
      ),
      c.custom(
        "client_net",
        (row) => {
          const price = target(row);
          if (price === 0) return 0;
          const royalty = (row.designer?.royalty ?? 0) / 100;
          const cogs = (row.pricing?.cogs ?? 0) / rate;
          const profit = price - price * (totalCostsPercent + royalty) - cogs;
          return Math.round((profit / price) * 1000) / 10;
        },
        (row) => {
          const price = target(row);
          if (price === 0) return <span className="text-ink-faint"></span>;
          const royalty = (row.designer?.royalty ?? 0) / 100;
          const cogs = (row.pricing?.cogs ?? 0) / rate;
          const profit = price - price * (totalCostsPercent + royalty) - cogs;
          return (
            <span className="tabular">
              {((profit / price) * 100).toFixed(1)}%
              <span className="ml-1 text-[11px] text-ink-faint">
                {Math.round(profit)} {currency}
              </span>
            </span>
          );
        },
        { headerKey: "pricing.client-net", priority: 3 },
      ),
    ];
  }, [productsQuery.data, clientQuery.data, globalMargin, t]);

  const clientOptions = (clientListQuery.data ?? [])
    .map((row) => ({ value: String(row.cli_id), label: clientLabel(row) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <>
      <PageHeader
        title={t("navigation.item.pricing-products", "Pricing")}
        subtitle={t("pricing.subtitle", "Prices, costs and margins, per client.")}
      />

      <DataTable
        data={productsQuery.data?.rows ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={productsQuery.isLoading || (clientId !== "" && clientQuery.isLoading)}
        // The client is what the grid is showing, so it sits beside the search
        // like every other grid-level control, not up in the page header.
        toolbarLead={
          <div className="flex items-center gap-2">
            <label htmlFor="pricing-client" className="text-[12px] text-ink-muted">
              {t("column.client", "Client")}
            </label>
            <Select
              id="pricing-client"
              value={clientId}
              placeholder={t("pricing.no-client", "No client")}
              options={clientOptions}
              onChange={(event) => setClientId(event.target.value)}
              className="h-8 w-56"
            />
          </div>
        }
        actions={[
          {
            label: t("pricing.export", "Export"),
            icon: <Download className="size-4" />,
            disabled: busy !== null || clientId === "",
            onClick: () =>
              void startExport(
                "pricing-export",
                () => pricing.exportPricing([Number(clientId)]),
                t("job.download.failed", "Download failed"),
              ),
          },
        ]}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
